import { Injectable, NgZone } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom, from, Observable, of, throwError } from 'rxjs';
import { map, tap, catchError, finalize, shareReplay, timeout } from 'rxjs/operators';
import { User, UserRole, UserPrivilege, LoginRequest, RegisterRequest, AuthResponse, AuthState, LoginOtpChallenge, LoginOtpResend } from '../models/user.model';
import { environment } from '../../../environments/environment';

/**
 * Not a credential: only records that this browser signed in, so a page load
 * asks /auth/refresh to restore the session instead of probing for every
 * anonymous visitor. The access token itself lives in memory only, and the
 * refresh token in an HttpOnly cookie script cannot read.
 */
const SESSION_HINT_KEY = 'bsfms_has_session';
/** Pre-refresh builds kept the whole auth state, token included, here. */
const LEGACY_AUTH_STATE_KEY = 'auth_state';
/** Cross-tab lock so only one tab rotates the shared refresh cookie at a time. */
const REFRESH_LOCK_NAME = 'bsfms-auth-refresh';
/** Renew the access token this long before it expires. */
const REFRESH_AHEAD_SECONDS = 120;
/** Idle logout used until the API sends its own (idleTimeoutSeconds). */
const DEFAULT_IDLE_TIMEOUT_MS = 10 * 60 * 1000;

/** Cookie-authenticated calls must carry this (server-side CSRF check). */
const COOKIE_AUTH_OPTIONS = {
    withCredentials: true,
    headers: new HttpHeaders({ 'X-Requested-With': 'XMLHttpRequest' })
};

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private apiUrl = environment.apiUrl;

    private authStateSubject = new BehaviorSubject<AuthState>({
        isAuthenticated: false,
        user: null,
        token: null
    });

    public authState$ = this.authStateSubject.asObservable();

    /** The refresh in progress in this tab; concurrent callers share it. */
    private refreshInFlight$: Observable<string> | null = null;
    private refreshTimer: any = null;
    /** The API's idle timeout, so the UI signs out when the server would. */
    private idleTimeoutMs = DEFAULT_IDLE_TIMEOUT_MS;

    constructor(private http: HttpClient, private ngZone: NgZone) {
        console.log('AuthService initialized');
        // Drop any token an older build left in localStorage.
        this.removeStorage(localStorage, LEGACY_AUTH_STATE_KEY);
    }

    /**
     * Restore the session after a page load or in a new tab, using the
     * refresh cookie. Run from APP_INITIALIZER — not the constructor, since an
     * HTTP call there would re-enter AuthService through the interceptor.
     * Never rejects: without a session the app simply starts signed out.
     */
    restoreSession(): Promise<void> {
        if (!this.readStorage(localStorage, SESSION_HINT_KEY)) {
            return Promise.resolve();
        }
        return firstValueFrom(this.refreshAccessToken().pipe(
            timeout(10000),
            map(() => undefined),
            catchError(() => of(undefined))
        ));
    }

    /**
     * Get a new access token from the refresh cookie. Concurrent callers in
     * this tab share one request, and a Web Lock serialises tabs, so the
     * rotating cookie is never presented by two refreshes at once. On an auth
     * failure the local session is cleared; on a network/server error it is
     * kept and the error passed on.
     */
    refreshAccessToken(): Observable<string> {
        if (!this.refreshInFlight$) {
            this.refreshInFlight$ = from(this.withRefreshLock(() => firstValueFrom(
                this.http.post<any>(`${this.apiUrl}/auth/refresh`, {}, COOKIE_AUTH_OPTIONS)
            ))).pipe(
                map(response => {
                    const user = this.mapUser(response.user);
                    this.setIdleTimeout(response.idleTimeoutSeconds);
                    this.setAuthState(user, response.token);
                    this.cachePrivileges(response.user.privileges || []);
                    console.log('🔄 Session refreshed:', user.username);
                    return response.token as string;
                }),
                catchError(error => {
                    if (AuthService.isAuthFailure(error)) {
                        this.clearAuthState();
                    }
                    return throwError(() => error);
                }),
                finalize(() => { this.refreshInFlight$ = null; }),
                shareReplay({ bufferSize: 1, refCount: false })
            );
        }
        return this.refreshInFlight$;
    }

    /** True when the server refused the session (as opposed to being unreachable). */
    static isAuthFailure(error: any): boolean {
        return error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403);
    }

    /**
     * Tell the API the user is active, so its idle timeout does not end a
     * session in which the user works without triggering API calls.
     */
    reportActivity(): void {
        if (!this.getToken()) return;
        this.http.post(`${this.apiUrl}/auth/activity`, {}).subscribe({ error: () => { /* 401 handled by the interceptor */ } });
    }

    /**
     * Step 1 of sign-in. A correct password on the central API does not sign in
     * yet: it returns an OTP challenge (code emailed to the user) that
     * verifyLoginOtp() completes. Local deployments, and environments with
     * login MFA off, sign in directly.
     */
    login(credentials: LoginRequest): Observable<AuthResponse | LoginOtpChallenge> {
        // COOKIE_AUTH_OPTIONS: X-Requested-With asks the API for a refreshable
        // browser session, and withCredentials lets the browser store its cookie.
        return this.http.post<any>(`${this.apiUrl}/auth/login`, credentials, COOKIE_AUTH_OPTIONS).pipe(
            map(response => response.otpRequired ? response as LoginOtpChallenge : this.completeLogin(response)),
            catchError(error => {
                console.error('❌ Login failed:', error);
                const errorMessage = error.error?.error || error.message || 'Login failed. Please check your credentials.';
                return throwError(errorMessage);
            })
        );
    }

    /** Step 2 of sign-in: the OTP from the email. Errors carry the API's code (OTP_EXPIRED, OTP_LOCKED, ...). */
    verifyLoginOtp(otpToken: string, otp: string): Observable<AuthResponse> {
        return this.http.post<any>(`${this.apiUrl}/auth/login/verify-otp`, { otpToken, otp }, COOKIE_AUTH_OPTIONS).pipe(
            map(response => this.completeLogin(response)),
            catchError(error => throwError(() => ({
                message: error.error?.error || 'Could not verify the OTP. Please try again.',
                code: error.error?.code
            })))
        );
    }

    resendLoginOtp(otpToken: string): Observable<LoginOtpResend> {
        return this.http.post<LoginOtpResend>(`${this.apiUrl}/auth/login/resend-otp`, { otpToken }).pipe(
            catchError(error => throwError(() => ({
                message: error.error?.error || 'Could not resend the OTP. Please try again.',
                code: error.error?.code,
                resendIn: error.error?.resendIn
            })))
        );
    }

    private completeLogin(response: any): AuthResponse {
        console.log('✅ Login successful:', response.user.username, '| Role:', response.user.role);
        // Backend returns { user, token, expiresIn }; the refresh token is an HttpOnly cookie.
        const user = this.mapUser(response.user);

        const authResponse: AuthResponse = {
            user: user,
            token: response.token,
            expiresIn: response.expiresIn
        };

        this.setIdleTimeout(response.idleTimeoutSeconds);
        this.setAuthState(user, response.token);
        this.fetchMyPrivileges(response.token);
        return authResponse;
    }

    register(data: RegisterRequest): Observable<User> {
        return this.http.post<any>(`${this.apiUrl}/auth/register`, {
            username: data.username,
            email: data.email,
            password: data.password,
            firstName: data.firstName,
            lastName: data.lastName,
            department: data.department
        }).pipe(
            map(response => {
                const user: User = {
                    id: response.user.id.toString(),
                    username: response.user.username,
                    email: response.user.email,
                    firstName: response.user.firstName,
                    lastName: response.user.lastName,
                    role: this.mapRoleFromBackend(response.user.role),
                    department: response.user.department,
                    createdDate: new Date(response.user.createdAt),
                    isActive: response.user.isActive
                };

                // Registration returns no token and does not sign the user in.
                return user;
            }),
            catchError(error => {
                console.error('Registration error:', error);
                return throwError(error.error?.error || 'Registration failed.');
            })
        );
    }

    /**
     * Sign out of this browser only: ends this browser's server session (the
     * refresh cookie) and clears local state. Used by the 401 handler and the
     * idle timeout, which must not sign out other devices.
     */
    logout(): void {
        if (this.getToken() || this.readStorage(localStorage, SESSION_HINT_KEY)) {
            this.endServerSession();
        }
        this.clearLocalSession();
    }

    /**
     * User-initiated logout (the Logout buttons). Tells the API to revoke the
     * token — which ends this user's sessions on every device — then clears
     * local state.
     */
    signOut(): void {
        const token = this.getToken();
        if (token) {
            // Fire-and-forget with the token captured now: local sign-out must
            // neither wait on the API nor fail because of it. withCredentials so
            // the response can clear the refresh cookie. If the token was
            // already expired, at least end this browser's session.
            this.http.post(`${this.apiUrl}/auth/logout`, {}, {
                withCredentials: true,
                headers: new HttpHeaders({ Authorization: `Bearer ${token}` })
            }).subscribe({ error: () => this.endServerSession() });
        } else {
            this.endServerSession();
        }
        this.clearLocalSession();
    }

    /**
     * Swap in a fresh token for the signed-in user. Used after changing your own
     * password: the API revokes every older token and returns a new one for
     * this session.
     */
    /** Emails a reset link. The API answers the same whether or not the email exists. */
    forgotPassword(email: string): Observable<string> {
        return this.http.post<{ message: string }>(`${this.apiUrl}/auth/forgot-password`, { email }).pipe(
            map(response => response.message),
            catchError(error => throwError(() => error.error?.error || 'Could not send the reset link. Please try again.'))
        );
    }

    /** Sets a new password using the token from the reset email link. */
    resetPassword(token: string, newPassword: string): Observable<string> {
        return this.http.post<{ message: string }>(`${this.apiUrl}/auth/reset-password`, { token, newPassword }).pipe(
            map(response => response.message),
            catchError(error => throwError(() => error.error?.error || 'Could not reset the password. Please try again.'))
        );
    }

    replaceToken(token: string): void {
        const user = this.authStateSubject.value.user;
        if (user && token) {
            this.setAuthState(user, token);
        }
    }

    /** Revoke this browser's server session and clear its refresh cookie. */
    private endServerSession(): void {
        this.http.post(`${this.apiUrl}/auth/end-session`, {}, COOKIE_AUTH_OPTIONS)
            .subscribe({ error: () => { /* nothing left to end */ } });
    }

    private clearLocalSession(): void {
        // Drop the per-session X-Session-Id binding so the next login gets a
        // fresh session id (auth interceptor will lazily re-create it).
        this.removeStorage(sessionStorage, 'bsfms_session_id');
        this.removeStorage(sessionStorage, 'bsfms_session_binding');
        // Older builds bound the session id to a copy of the token here.
        this.removeStorage(sessionStorage, 'bsfms_session_token');
        this.clearAuthState();
    }

    /**
     * Run fn while holding the cross-tab refresh lock. Browsers without the
     * Web Locks API run it directly; the server tolerates the brief race.
     */
    private withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
        const locks = (navigator as any).locks;
        return locks?.request ? locks.request(REFRESH_LOCK_NAME, () => fn()) : fn();
    }

    /** Renew the access token shortly before it expires, while signed in. */
    private scheduleRefresh(token: string): void {
        this.clearRefreshTimer();
        const secondsLeft = AuthService.secondsUntilExpiry(token);
        if (secondsLeft === null) return;
        const delayMs = Math.max(30, secondsLeft - REFRESH_AHEAD_SECONDS) * 1000;
        this.ngZone.runOutsideAngular(() => {
            this.refreshTimer = setTimeout(() => this.ngZone.run(() => {
                this.refreshTimer = null;
                if (!this.getToken()) return;
                // On failure the token is kept until it expires; the next
                // request's 401 then goes through the interceptor as usual.
                this.refreshAccessToken().subscribe({ error: () => { /* see above */ } });
            }), delayMs);
        });
    }

    private clearRefreshTimer(): void {
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
            this.refreshTimer = null;
        }
    }

    /** Seconds until the JWT's exp, or null if it cannot be read. */
    private static secondsUntilExpiry(token: string): number | null {
        try {
            const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
            return typeof payload.exp === 'number' ? payload.exp - Math.floor(Date.now() / 1000) : null;
        } catch {
            return null;
        }
    }

    private mapUser(u: any): User {
        return {
            id: u.id.toString(),
            username: u.username,
            email: u.email,
            firstName: u.firstName,
            lastName: u.lastName,
            role: this.mapRoleFromBackend(u.role),
            isAdmin: u.isAdmin === true,
            department: u.department,
            createdDate: new Date(u.createdAt || new Date()),
            isActive: u.isActive
        };
    }

    private readStorage(storage: Storage, key: string): string | null {
        try {
            return storage.getItem(key);
        } catch {
            return null;
        }
    }

    private writeStorage(storage: Storage, key: string, value: string): void {
        try {
            storage.setItem(key, value);
        } catch { /* ignore storage errors */ }
    }

    private removeStorage(storage: Storage, key: string): void {
        try {
            storage.removeItem(key);
        } catch { /* ignore storage errors */ }
    }

    isAuthenticated(): boolean {
        return this.authStateSubject.value.isAuthenticated;
    }

    getCurrentUser(): User | null {
        return this.authStateSubject.value.user;
    }

    getToken(): string | null {
        return this.authStateSubject.value.token;
    }

    /** Idle time (ms) after which the UI signs out; set by the API on login/refresh. */
    getIdleTimeoutMs(): number {
        return this.idleTimeoutMs;
    }

    /** Take the API's idle timeout; a missing or invalid value keeps the default. */
    private setIdleTimeout(seconds: any): void {
        const value = Number(seconds);
        this.idleTimeoutMs = Number.isFinite(value) && value > 0 ? value * 1000 : DEFAULT_IDLE_TIMEOUT_MS;
    }

    // UserRole.Admin is satisfied by the API's isAdmin flag, never by the
    // role's name (V-API-01). Other roles still match by name.
    hasRole(role: UserRole): boolean {
        if (role === UserRole.Admin) return this.isAdmin();
        const user = this.getCurrentUser();
        return user ? user.role === role : false;
    }

    hasAnyRole(roles: UserRole[]): boolean {
        if (roles.includes(UserRole.Admin) && this.isAdmin()) return true;
        const user = this.getCurrentUser();
        // return user ? roles.includes(user.role) : false;
        return user ? roles.some(r => r !== UserRole.Admin && r === user.role) : false;
    }

    isAdmin(): boolean {
        // return this.hasRole(UserRole.Admin);
        return this.getCurrentUser()?.isAdmin === true;
    }

    canEdit(): boolean {
        return this.hasAnyRole([UserRole.Admin, UserRole.Manager, UserRole.User]);
    }

    canDelete(): boolean {
        return this.hasAnyRole([UserRole.Admin, UserRole.Manager]);
    }

    canUpload(): boolean {
        return this.hasAnyRole([UserRole.Admin, UserRole.Manager, UserRole.User]);
    }

    getUserPrivilegeModules(): string[] {
        const privileges = this.authStateSubject.value.privileges || [];
        return [...new Set(privileges.filter(p => p.module).map(p => p.module.toLowerCase()))];
    }

    hasPrivilegeModule(modules: string[]): boolean {
        const userModules = this.getUserPrivilegeModules();
        return modules.some(m => userModules.includes(m.toLowerCase()));
    }

    hasPrivilege(privilegeName: string): boolean {
        if (this.isAdmin()) return true;
        const privileges = this.authStateSubject.value.privileges || [];
        return privileges.some(p => p.name.toLowerCase() === privilegeName.toLowerCase());
    }

    private fetchMyPrivileges(token: string): void {
        const headers = new HttpHeaders({
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache'
        });

        this.http.get<any>(`${this.apiUrl}/auth/me?_t=${Date.now()}`, { headers }).subscribe(
            (response) => {
                this.applyMeResponse(response);
            },
            (error) => {
                console.error('Failed to fetch user privileges:', error);
            }
        );
    }

    /**
     * Apply a /auth/me response: cache the privileges and refresh the user's
     * isAdmin flag. The flag is re-read here (not only at login) so a session
     * saved before the field existed, or whose role was changed by an admin,
     * picks it up on the next page load without signing in again.
     */
    private applyMeResponse(response: any): void {
        const state = this.authStateSubject.value;
        if (state.user && typeof response?.isAdmin === 'boolean' && state.user.isAdmin !== response.isAdmin) {
            const updated: AuthState = { ...state, user: { ...state.user, isAdmin: response.isAdmin } };
            this.authStateSubject.next(updated);
        }
        this.cachePrivileges(response?.privileges || []);
    }

    private cachePrivileges(privileges: any[]): void {
        const mapped: UserPrivilege[] = (privileges || []).map(p => {
            if (typeof p === 'string') {
                // Format: "module.action" e.g. "document.view"
                const dotIndex = p.indexOf('.');
                return {
                    name: p,
                    module: dotIndex > -1 ? p.substring(0, dotIndex) : p
                };
            }
            return { name: p.name, module: p.module };
        });
        const currentState = this.authStateSubject.value;
        const updatedState: AuthState = { ...currentState, privileges: mapped };
        this.authStateSubject.next(updatedState);
        console.log('🔑 Privileges loaded:', mapped.length, '| Modules:', [...new Set(mapped.filter(p => p.module).map(p => p.module))]);
    }

    // Get current user from backend (verify token)
    getCurrentUserFromBackend(): Observable<User> {
        return this.http.get<any>(`${this.apiUrl}/auth/me`, {
            headers: this.getAuthHeaders()
        }).pipe(
            map(response => {
                const user: User = {
                    id: response.id.toString(),
                    username: response.username,
                    email: response.email,
                    firstName: response.firstName,
                    lastName: response.lastName,
                    role: this.mapRoleFromBackend(response.role),
                    isAdmin: response.isAdmin === true,
                    department: response.department,
                    createdDate: new Date(response.createdAt),
                    isActive: response.isActive
                };
                return user;
            }),
            catchError(error => {
                console.error('Get current user error:', error);
                this.clearAuthState();
                return throwError(error);
            })
        );
    }

    // Get all users (admin only)
    getAllUsers(): Observable<User[]> {
        if (!this.isAdmin()) {
            return throwError('Unauthorized');
        }

        return this.http.get<any[]>(`${this.apiUrl}/users`, {
            headers: this.getAuthHeaders()
        }).pipe(
            map(users => users.map(u => ({
                id: u.id.toString(),
                username: u.username,
                email: u.email,
                firstName: u.firstName,
                lastName: u.lastName,
                role: this.mapRoleFromBackend(u.role),
                department: u.department,
                createdDate: new Date(u.createdAt),
                isActive: u.isActive
            }))),
            catchError(error => {
                console.error('Get users error:', error);
                return throwError(error.error?.error || 'Failed to fetch users');
            })
        );
    }

    // Update user role (admin only)
    updateUserRole(userId: string, newRole: UserRole): Observable<User> {
        if (!this.isAdmin()) {
            return throwError('Unauthorized');
        }

        return this.http.put<any>(`${this.apiUrl}/users/${userId}`, {
            roleId: this.mapRoleToBackend(newRole)
        }, {
            headers: this.getAuthHeaders()
        }).pipe(
            map(response => ({
                id: response.id.toString(),
                username: response.username,
                email: response.email,
                firstName: response.firstName,
                lastName: response.lastName,
                role: this.mapRoleFromBackend(response.role),
                department: response.department,
                createdDate: new Date(response.createdAt),
                isActive: response.isActive
            })),
            catchError(error => {
                console.error('Update user role error:', error);
                return throwError(error.error?.error || 'Failed to update user role');
            })
        );
    }

    /**
     * Auth state (token included) is held in memory only. Privileges already
     * loaded are kept while the same user's token is renewed.
     */
    private setAuthState(user: User, token: string): void {
        const current = this.authStateSubject.value;
        const authState: AuthState = {
            isAuthenticated: true,
            user: user,
            token: token,
            ...(current.user?.id === user.id && current.privileges ? { privileges: current.privileges } : {})
        };

        console.log('💾 Auth state set:', user.username);
        this.authStateSubject.next(authState);
        this.writeStorage(localStorage, SESSION_HINT_KEY, '1');
        this.scheduleRefresh(token);
    }

    private clearAuthState(): void {
        const authState: AuthState = {
            isAuthenticated: false,
            user: null,
            token: null
        };

        this.clearRefreshTimer();
        this.authStateSubject.next(authState);
        this.removeStorage(localStorage, LEGACY_AUTH_STATE_KEY);
        this.removeStorage(localStorage, SESSION_HINT_KEY);
        this.removeStorage(localStorage, 'last_activity');
    }

    private getAuthHeaders(): HttpHeaders {
        const token = this.getToken();
        return new HttpHeaders({
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        });
    }

    private mapRoleFromBackend(backendRole: string): UserRole {
        switch (backendRole.toLowerCase()) {
            case 'admin':
                return UserRole.Admin;
            case 'manager':
                return UserRole.Manager;
            case 'user':
                return UserRole.User;
            case 'viewer':
                return UserRole.Viewer;
            default:
                return UserRole.User;
        }
    }

    private mapRoleToBackend(role: UserRole): number {
        // Role IDs from database: admin=1, manager=2, user=3, viewer=4
        switch (role) {
            case UserRole.Admin:
                return 1;
            case UserRole.Manager:
                return 2;
            case UserRole.User:
                return 3;
            case UserRole.Viewer:
                return 4;
            default:
                return 3;
        }
    }
}
